if (!customElements.get('base-video-reels')) {
  customElements.define(
    'base-video-reels',
    class BaseVideoReels extends HTMLElement {
      constructor() {
        super();

        this.track = this.querySelector('[data-reels-track]');
        this.slides = Array.from(this.querySelectorAll('[data-reels-slide]'));
        this.prevButton = this.querySelector('[data-reels-prev]');
        this.nextButton = this.querySelector('[data-reels-next]');

        // O autoplay NÃO olha para prefers-reduced-motion. Vídeo de vitrine é
        // conteúdo, e a régua parada vira uma fileira de retângulos com botão de
        // play que ninguém clica — o botão de pausa em cada card continua ali
        // para quem quiser parar.
        this.autoplay = this.dataset.autoplay === 'true';

        // Slides pausados de propósito pelo cliente não voltam a tocar sozinhos.
        this.manuallyPaused = new WeakSet();
      }

      connectedCallback() {
        if (!this.track || this.slides.length === 0) return;

        this.slides.forEach((slide) => this.bindSlide(slide));

        this.prevButton?.addEventListener('click', () => this.scrollByCard(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByCard(1));

        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        this.updateNavState();

        if (this.autoplay) this.observeVisibility();

        // Pausa tudo quando a aba sai de foco: vídeo tocando escondido é só consumo de banda.
        // Na volta, a IntersectionObserver não dispara de novo (nada mudou de
        // posição), então os que estavam em tela precisam ser religados à mão.
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.pauseAll();
            return;
          }
          if (this.autoplay) this.visibleSlides?.forEach((slide) => this.play(slide));
        });
      }

      bindSlide(slide) {
        const video = slide.querySelector('video');
        if (!video) return;

        const playButton = slide.querySelector('[data-reels-play]');
        const soundButton = slide.querySelector('[data-reels-sound]');

        video.muted = true;
        video.setAttribute('playsinline', '');

        video.addEventListener('play', () => {
          slide.setAttribute('data-playing', 'true');
          if (playButton?.dataset.pauseLabel) playButton.setAttribute('aria-label', playButton.dataset.pauseLabel);
        });
        video.addEventListener('pause', () => {
          slide.removeAttribute('data-playing');
          if (playButton?.dataset.playLabel) playButton.setAttribute('aria-label', playButton.dataset.playLabel);
        });

        playButton?.addEventListener('click', () => this.togglePlay(slide));
        soundButton?.addEventListener('click', () => this.toggleSound(slide));
        video.addEventListener('click', () => this.togglePlay(slide));
      }

      getVideo(slide) {
        return slide.querySelector('video');
      }

      play(slide) {
        const video = this.getVideo(slide);
        if (!video || this.manuallyPaused.has(slide)) return;

        // preload="metadata" traz só o cabeçalho de cada vídeo; o arquivo em si
        // só baixa quando o card entra em tela e o play acontece.
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }

      pauseAll(except) {
        this.slides.forEach((slide) => {
          if (slide === except) return;
          const video = this.getVideo(slide);
          if (video && !video.paused) video.pause();
        });
      }

      togglePlay(slide) {
        const video = this.getVideo(slide);
        if (!video) return;

        // Sem pausar os vizinhos: os cards tocam juntos e todos mudos, então
        // parar os outros só tiraria movimento da régua sem ganhar nada.
        if (video.paused) {
          this.manuallyPaused.delete(slide);
          this.play(slide);
        } else {
          this.manuallyPaused.add(slide);
          video.pause();
        }
      }

      toggleSound(slide) {
        const video = this.getVideo(slide);
        const button = slide.querySelector('[data-reels-sound]');
        if (!video) return;

        const willUnmute = slide.dataset.muted === 'true';

        // Som é exclusivo: dois reels tocando junto viram barulho.
        if (willUnmute) {
          this.slides.forEach((other) => {
            if (other === slide) return;
            other.dataset.muted = 'true';
            const otherVideo = this.getVideo(other);
            if (otherVideo) otherVideo.muted = true;
          });
        }

        slide.dataset.muted = willUnmute ? 'false' : 'true';
        video.muted = !willUnmute;

        if (button) {
          const key = willUnmute ? 'soundOff' : 'soundOn';
          if (button.dataset[key]) button.setAttribute('aria-label', button.dataset[key]);
        }

        if (willUnmute && video.paused) {
          this.manuallyPaused.delete(slide);
          this.play(slide);
        }
      }

      scrollByCard(direction) {
        const slide = this.slides[0];
        if (!slide) return;

        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = slide.getBoundingClientRect().width + gap;

        // Suave sempre: sem a transição o card seguinte simplesmente aparece no
        // lugar do anterior e não fica claro que a régua andou.
        this.track.scrollBy({
          left: direction * distance,
          behavior: 'smooth',
        });
      }

      onScroll() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.updateNavState();
          this.scrollRaf = null;
        });
      }

      updateNavState() {
        if (!this.prevButton || !this.nextButton) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const maxScroll = scrollWidth - clientWidth;
        // Direção RTL usa scrollLeft negativo; o valor absoluto normaliza.
        const position = Math.abs(scrollLeft);

        this.prevButton.disabled = position <= 1;
        this.nextButton.disabled = position >= maxScroll - 1;
      }

      /**
       * Toca todo reel que estiver em tela, não só o mais visível: a régua é uma
       * parede de vídeos, e um único card se mexendo entre quatro parados parece
       * defeito.
       *
       * O root é a viewport, não o trilho. A IntersectionObserver já recorta o
       * alvo pelo overflow dos ancestrais, então um card que saiu na horizontal
       * conta como fora de tela do mesmo jeito — e, de brinde, a régua inteira
       * fica quieta enquanto a seção está abaixo da dobra, em vez de baixar
       * vídeo que ninguém está vendo.
       *
       * A margem de 200px começa a tocar um pouco antes de entrar, para o card
       * não aparecer congelado no poster.
       */
      observeVisibility() {
        this.visibleSlides = new Set();

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = this.getVideo(entry.target);

              if (entry.isIntersecting) {
                this.visibleSlides.add(entry.target);
                this.play(entry.target);
              } else {
                this.visibleSlides.delete(entry.target);
                if (video && !video.paused) video.pause();
              }
            });
          },
          { threshold: 0.25, rootMargin: '200px 0px' }
        );

        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );
}
